import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, NativeModules, Platform, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { analyticsEvents } from '../../services/firebase/analytics-events.service';
import { getSocket } from '../../services/socket';
import { updateProfileRequest, updateUserLevel } from '../../store/auth/authActions';
import { forgetOpenTable, rememberOpenTable } from '../../utils/helpers/activeTableRestore';
import { playSound } from '../../utils/sound';
import GameTable from '../Components/Game/GameTable';
import BecomeVipModal from '../Components/Modals/BecomeVipModal';
import InvitePlayerToTableModal from '../Components/Modals/InvitePlayerToTableModal';
import LeveledUpNotificationModal from '../Components/Modals/LeveledUpNotificationModal';
import NotEnoughChipsModal from '../Components/Modals/NotEnoughChipsModal';
import { RootStackParamList } from '../navigator';
type GameScreenProps = NativeStackScreenProps<RootStackParamList, 'Game'>;

const GameScreen: React.FC = () => {
	const route = useRoute<GameScreenProps['route']>();
	const navigation = useNavigation<GameScreenProps['navigation']>();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { tableId, legacyRoomId, roomSlug, isTutorial, tableName, isTournamentRoom } =
		route.params || {};
	const [showInvitesModal, setShowInvitesModal] = useState(false);
	const [showBecomeVipModal, setShowBecomeVipModal] = useState(false);
	const [showNotEnoughChipsModal, setShowNotEnoughChipsModal] = useState(false);
	const [showLeveledUpNotification, setShowLeveledUpNotification] = useState(false);
	const [newLevel, setNewLevel] = useState(0);
	const stableTimestamp = useRef((route.params && route.params.timestamp) || Date.now()).current;
	const auth = useSelector((s: any) => s.auth);
	const playerId = auth?.user?.id || '';
	const username = auth?.user?.username || '';

	useEffect(() => {
		if (Platform.OS === 'web') return;

		NativeModules.GameOrientation?.allowGameOrientations();
		return () => NativeModules.GameOrientation?.lockPortrait();
	}, []);

	useEffect(() => {
		analyticsEvents.trackOpenTable(roomSlug, tableId);
	}, [tableId, roomSlug]);

	// Lets ActiveTableRestoreHost reopen this table if the page is reloaded while it is open.
	useEffect(() => {
		if (isTutorial || tableId == null) return;
		const token = rememberOpenTable({
			tableId,
			legacyRoomId,
			roomSlug,
			tableName,
			isTournamentRoom,
		});
		return () => forgetOpenTable(token);
	}, [tableId, legacyRoomId, roomSlug, tableName, isTournamentRoom, isTutorial]);

	useEffect(() => {
		if (!playerId || !username) return;
		const socket = getSocket(String(playerId), String(username));
		if (!socket) return;

		const handleLevelUp = (payload: any) => {
			const data = payload.data || payload;
			const level = Number(data.level);
			console.log('[TOURNAMENT] levelUP received', {
				level,
				isTournamentRoom,
				tableId,
			});
			if (!Number.isFinite(level)) return;

			dispatch(updateUserLevel(level));
			setNewLevel(level);
			setShowLeveledUpNotification(true);
		};

		socket.on('levelUP', handleLevelUp);

		return () => {
			socket.off('levelUP', handleLevelUp);
		};
	}, [dispatch, playerId, username, isTournamentRoom, tableId]);

	const handleGameEvent = useCallback(
		(event: string, data?: any) => {
			console.log('[TOURNAMENT] Game event received:', {
				event,
				data,
				tableId,
				isTournamentRoom,
				roomSlug,
			});

			switch (event) {
				case 'goToLobby': {
					console.log('[TOURNAMENT] goToLobby event - navigating to Lobby', {
						tableId,
						isTournamentRoom,
						roomSlug,
						timestamp: new Date().toISOString(),
					});
					// Leaving a table always lands on the location screen with a clean
					// history.
					navigation.reset({ index: 1, routes: [{ name: 'Main' }, { name: 'Lobby' }] });
					break;
				}

				case 'becomeVIP':
					setShowBecomeVipModal(true);
					break;

				case 'notEnoughChips':
					setShowNotEnoughChipsModal(true);
					break;

				case 'buyChips':
					navigation.navigate('BuyPackages', { activeTab: 'chips' });
					break;

				case 'invite':
					setShowInvitesModal(true);
					break;

				case 'setSound': {
					const soundEnabled = typeof data === 'boolean' ? data : Boolean(data?.setSound);
					// Audible confirmation when the toggle turns sound on.
					if (soundEnabled) playSound('ButtonPressed', { force: true });
					if (typeof data === 'boolean' || typeof data?.setSound === 'boolean') {
						dispatch(updateProfileRequest({ isSoundEffectsEnabled: soundEnabled }));
					}
					break;
				}

				case 'openNotifications':
					navigation.navigate('Messages');
					break;

				case 'showBlackscreenAlert':
				case 'hideBlackscreenAlert':
					// Ignore on purpose, legacy stuff
					break;

				case 'tableDoesNotExist':
					Alert.alert(t('T_SITE_TABLE_NON_EXISTING_TABLE'), undefined, [
						{ text: t('T_OK', { defaultValue: 'OK' }), onPress: () => navigation.goBack() },
					]);
					break;

				default:
					console.log('Unhandled game event:', event, data);
			}
		},
		[navigation, isTournamentRoom, roomSlug, tableId, dispatch, t],
	);

	const handleCloseNotEnoughChipsModal = useCallback(() => {
		setShowNotEnoughChipsModal(false);
	}, []);

	const handleBuyChips = useCallback(() => {
		setShowNotEnoughChipsModal(false);
		navigation.navigate('BuyPackages', { activeTab: 'chips' });
	}, [navigation]);

	return (
		<View style={styles.container}>
			<GameTable
				key={`${tableId}-${stableTimestamp}`}
				tableId={tableId}
				legacyRoomId={legacyRoomId}
				roomSlug={roomSlug}
				isTutorial={isTutorial}
				onGameEvent={handleGameEvent}
				isTournamentRoom={isTournamentRoom}
			/>
			{showInvitesModal && (
				<InvitePlayerToTableModal
					onClose={() => setShowInvitesModal(false)}
					tableId={tableId}
					tableName={tableName}
				/>
			)}
			{showBecomeVipModal && <BecomeVipModal onClose={() => setShowBecomeVipModal(false)} />}

			{showLeveledUpNotification && newLevel > 0 && (
				<LeveledUpNotificationModal
					visible={showLeveledUpNotification}
					level={newLevel}
					onClose={() => setShowLeveledUpNotification(false)}
				/>
			)}

			<NotEnoughChipsModal
				visible={showNotEnoughChipsModal}
				onClose={handleCloseNotEnoughChipsModal}
				onBuy={handleBuyChips}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: '#000',
	},
});

export default GameScreen;
